import * as React from "react";
import {
    Accordion,
    AccordionProps,
    AccordionTab,
    AccordionTabProps
} from "@bizform/primereact/components/accordion/Accordion";
import { Static } from "@bizform/type/decorator/Static.decorator";
import { DomAttribute } from "@bizform/type/model/dom/DomAttribute.model";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementBorderType,
    ComponentElementDisplayMode,
    ComponentElementOverflowType,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { ComponentHelper, Debug, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../../bf_component/AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type TabProps = AccordionTabProps & {
    key: string;
    useCustomTitle?: boolean;
    titleData?: string;
};

type Props = AccordionProps & {
    activeContainerKey: any;
    accordionTabProps: Array<TabProps>;
};

type WrappedCompState = Props & ComponentElementOverflowType & ComponentElementBorderType & {
    width?: string;
    height?: string;
};

type WrappedFormState = {
    onTabChange?: string;
    onTabOpen?: string;
    onTabClose?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Hwansoon Park
 * @class BfAccordion
 * @description BfAccordion component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<null>>()
export class BfAccordion extends AbstractComponentElement<Props, State> {
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_ivax_sJa8J2f6GFyyeQnCICT5_eJaJO",
        category: ComponentCategory.PANEL,
        label: I18n.layout.accordion.label,
        description: I18n.layout.accordion.description,
        icon: "",
        property: null
    };

    /* for React */
    props: Props;
    state: State;
    private _accordion: any;
    private _needRender: boolean;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                activeIndex: [],
                activeContainerKey: [],
                accordionTabProps: props.accordionTabProps
            },
            wrappedFormState: {}
        };

        this._tab = this._tab.bind(this);
        this._setTabTitle = this._setTabTitle.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfAccordion)
    static init(): ComponentElement<null> {
        return;
    }

    focus(): void {
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            this._needRender = true;
            const newState = ComponentHelper.createStateWithProperties<State, { originalEvent: Event, index: number }>(
                property,
                this);
            const n = newState.wrappedCompState;
            if (this.accessibility) {
                n.multiple = true;
                n.activeIndex = [];
                if (!Array.isArray(n.accordionTabProps) ) {
                    n.accordionTabProps = [];
                }
                for (let i = 0; i < n.accordionTabProps.length; i++) {
                    n.activeIndex.push(i);
                }
            } else {
                n.multiple = n.multiple ? true : false;
                if (n.hasOwnProperty("activeIndex") && n.hasOwnProperty("accordionTabProps") && Array.isArray(n.accordionTabProps)) {
                    if (n.activeIndex + 1 <= n.accordionTabProps.length) {
                        n.activeIndex = [n.activeIndex];
                    } else {
                        n.activeIndex = [0];
                    }
                }
            }
            this.setState(newState);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    emitFormState(value: string): void {
    }

    emitContainerKey(value: string): void {
    }

    emitHasContainerKey(): void {
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    componentWillUnmount(): void {
        this._accordion = null;
    }

    componentDidUpdate(): void {
        if (this._accordion) {
            this._makeStyle();
        }
        if (this._accordion && this._accordion.container && this._needRender) {
            this.state.wrappedCompState.accordionTabProps.map(this._setTabTitle);
            this._needRender = false;
        }
    }

    private _setTabTitle(p: any, i: number) {
        if (this.accessibility) {
            const a: HTMLDivElement = this.parentDom.querySelector("#" + this.elementId + "_header_" + i);
            a.removeAttribute("href");
            a.removeAttribute("aria-controls");
            a.removeAttribute("aria-expanded");
            a.removeAttribute("role");
        }
        const atc: HTMLDivElement = this.parentDom.querySelector("#" + this.elementId + "_content_" + i + " .p-accordion-content");
        if (atc) {
            if (!atc.hasAttribute(DomAttribute.CONTAINER.toString())) {
                atc.setAttribute(DomAttribute.CONTAINER.toString(), p.key);
            }
            const h: HTMLElement = this.parentDom.querySelector("#" + this.elementId + "_header_" + i + " .p-accordion-header-text");
            if (p.useCustomTitle) {
                h.innerHTML = p.titleData;
            } else {
                h.textContent = p.titleData;
            }
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    private _onTabChangeEvent(e): void {
        if (this.accessibility) {
            return;
        }
        const newProps = {
            ...this.state.wrappedCompState
        };
        newProps.activeIndex = [];
        newProps.activeContainerKey = [];
        if (newProps.multiple) {
            newProps.activeIndex = e.index;
            e.index.map((x) => {
                newProps.activeContainerKey.push(newProps.accordionTabProps[x].key);
            });
        } else {
            if (e.index !== null) {
                newProps.activeIndex.push(e.index);
                newProps.activeContainerKey.push(newProps.accordionTabProps[e.index].key);
            }
        }
        this.setState({wrappedCompState: {
            ...newProps
        }}, this._callbackSetState);
    }

    private _callbackSetState(): void {
        const {activeContainerKey} = this.state.wrappedCompState;
        this.emitContainerKey(Array.isArray(activeContainerKey) ? "," + activeContainerKey.join(",") + "," : null);
    }

    private _onTabOpenEvent(event): void {
    }

    private _onTabCloseEvent(event): void {
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            /*
             * Accordion doesn't have readOnly, disabled properties.
             */
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                case ComponentElementDisplayMode.READONLY:
                case ComponentElementDisplayMode.DISABLED:
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: Props): React.ReactNode {
        if (newProps.accordionTabProps && newProps.accordionTabProps.length > 0) {
            return (
                <Accordion
                    {...newProps}
                    id={this.elementId}
                    className={"h-100"}
                    style={null}
                    ref={this._setRef}>
                    {newProps.accordionTabProps.map(this._tab)}
                </Accordion>
            );
        } else {
            const containerString = {[DomAttribute.CONTAINER.toString()]: ""};
            return (
                <div ref={this._setRef} {...containerString} style={{width: "100%"}}/>
            );
        }
    }

    private _tab(p: AccordionTabProps): React.ReactNode {
        return <AccordionTab {...p}></AccordionTab>;
    }

    private _render4Print(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };
        if (!newProps.multiple) {
            newProps.activeIndex = newProps.activeIndex[0];
        }
        return this._render(newProps);
    }

    private _setRef(ref) {
        if (ref) {
            this._accordion = ref;
            this.readyComponent();
            this._makeStyle();
        }
    }

    private _makeStyle(newProps = {... this.state.wrappedCompState}) {
        if (this._accordion && this._accordion.container) {
            let div = this._accordion.container.querySelectorAll(".p-accordion-content");
            if (div && div.length > 0) {
                for (let i = 0; i < div.length; i++) {
                    div[i].setAttribute("style", "");
                    for (let item in newProps.style) {
                        div[i].style[item] = newProps.style[item];
                    }
                    for (let item in newProps.accordionTabProps) {
                        if (i === Number(item)) {
                            div[i].setAttribute("class", `p-accordion-content ${newProps.accordionTabProps[item].contentClassName}`);
                        }
                    }
                }
            }
        }
    }
}